import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikHelpers } from 'formik';
import merge from 'lodash.merge';

import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import RefreshIcon from '@material-ui/icons/Refresh';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';

import compose from 'helpers/compose';
import FormSelectField from 'components/FormFields/FormSelectField';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import ControlButtons from 'components/ControlButtons';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import FormTextField from 'components/FormFields/FormTextField';
import Switch from 'components/FormFields/Switch';

import { AdvisorsStore, AdvisorData, initialValues } from 'stores/advisorsStore';
const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface OwnProps {
  opened: boolean;
  handleClose: () => void;
  advisor: { realm: string; name: string };
  refresh: () => void;
  setAdvisor: (advisor: { realm: string; name: string }) => void;
  setConfirmAction: (action: 'ACTIVATE' | 'REMOVE') => void;
  setConfirmDialog: (show: boolean) => void;
}

interface Stores {
  advisorsStore: AdvisorsStore;
}

type Props = OwnProps & Stores;

const EditAdvisorModal: React.FC<Props> = ({
  advisor,
  opened,
  handleClose,
  advisorsStore,
  refresh,
  setAdvisor,
  setConfirmAction,
  setConfirmDialog,
}) => {
  const { t } = useTranslation();
  let formSubmitting = false;
  const formKey = 'AdvisorForm';
  const { fetchAdvisor, handleSubmit, data } = advisorsStore;

  useEffect(() => {
    if (opened && advisor.name) {
      fetchAdvisor(advisor.realm, advisor.name);
    }
  }, [advisor.name, advisor.realm, fetchAdvisor, opened]);

  const onSubmit = async (values: AdvisorData, formikHelpers: FormikHelpers<AdvisorData>) => {
    const ok = await handleSubmit(advisor.realm, advisor.name, values, formikHelpers);
    if (ok) {
      handleClose();
      advisorsStore.updateLocalAdvisorData(
        advisor.realm,
        advisor.name,
        values.individualPortfolioEnabled,
        values.code,
        values.role,
        values.firstname,
        values.lastname,
      );
    }
  };

  return data ? (
    <>
      <Dialog maxWidth="md" open={opened}>
        <DialogHeader onClose={handleClose}>{t('backOffice_advisors_edit')}</DialogHeader>
        <DialogContent>
          <Formik initialValues={merge({}, initialValues, data)} onSubmit={onSubmit} enableReinitialize>
            {(formikProps) => {
              formSubmitting = formikProps.isSubmitting;
              return (
                <FormWithoutEnterSubmit id={t(formKey)}>

                  <Grid container>
                    <Grid item xs={12}>
                      <Button
                        color='primary'
                        variant='contained'
                        startIcon={<RefreshIcon />}
                        onClick={() => {
                          setConfirmAction('ACTIVATE');
                          setAdvisor({ realm: advisor.realm, name: advisor.name });
                          setConfirmDialog(true);
                        }}
                      >
                        {t('backOffice_advisors_activate')}
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                    <Divider />
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item>
                      <Typography variant="h1">{t('backOffice_advisors_basicData')}</Typography>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={6}>
                      <FormTextField name="firstname" label={t('general_name')} />
                    </Grid>
                    <Grid item xs={6}>
                      <FormTextField name="lastname" label={t('general_surname')} />
                    </Grid>
                    <Grid item xs={6}>
                      <FormTextField name={`degreePrefix`} label={t('general_degreePrefix')} />
                    </Grid>
                    <Grid item xs={6}>
                      <FormTextField name={`degreeSuffix`} label={t('general_degreeSuffix')} />
                    </Grid>
                    <Grid item xs={6}>
                      <FormTextField name="email" label={t('general_email')} type="email" />
                    </Grid>
                    <Grid item xs={6}>
                      <FormTextField name="phone" label={t('general_phone')} />
                    </Grid>
                    <Grid item xs={12} />
                  </Grid>

                  <Grid container>
                    <Grid item>
                      <Typography variant="h1">{t('backOffice_advisors_advisor')}</Typography>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={4}>
                      <FormTextField name={`companyNumber`} label={t('backOffice_advisors_number')} />
                    </Grid>
                    <Grid item xs={4}>
                      <FormTextField name={`code`} label={t('general_code')} />
                    </Grid>
                    <Grid item xs={4}>
                      <FormSelectField
                        name={`role`}
                        label={t('general_role')}
                        options={formikProps.values.options.role.map((role) => ({
                          value: role,
                          label: t(`role_${role}`),
                        }))}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormTextField name={`representedCompany`} label={t('general_representedCompany')} />
                    </Grid>
                    <Grid item xs={4}>
                      <FormTextField name={`representedCompanyNumber`} label={t('general_representedCompanyNumber')} />
                    </Grid>
                    <Grid item xs={12} />
                  </Grid>

                  <Grid container>
                    <Grid item>
                      <Typography variant="h1">{t('backOffice_advisors_address')}</Typography>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={6}>
                      <FormTextField name="address.street" label={t('general_street')} />
                    </Grid>
                    <Grid item xs={3}>
                      <FormTextField name="address.landRegistryNumber" label={t('general_landRegistryNumber')} />
                    </Grid>
                    <Grid item xs={3}>
                      <FormTextField name="address.houseNumber" label={t('general_houseNumber')} />
                    </Grid>
                    <Grid item xs={6}>
                      <FormTextField name="address.city" label={t('general_city')} />
                    </Grid>
                    <Grid item xs={3}>
                      <FormTextField name="address.postalCode" label={t('general_postalCode')} />
                    </Grid>
                    <Grid item xs={3}>
                      <FormSelectField
                        name={`address.country`}
                        label={t('general_country')}
                        options={formikProps.values.options.country.map((country) => ({
                          value: country,
                          label: t(`country_${country}`),
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12} />
                  </Grid>
                  <Grid container>
                    <Grid item>
                      <Typography variant="h1">{t('backOffice_advisors_misc')}</Typography>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item>
                      <Switch name={`customLogoEnabled`} label={t('backOffice_advisors_customLogoEnabled')} />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item>
                      <Switch
                        name={`individualPortfolioEnabled`}
                        label={t('backOffice_advisors_individualPortfolioEnabled')}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item>
                      <Switch name={`currencyEur`} label={t('backOffice_advisors_currencyEur')} />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item>
                      <Switch name={`externalProducts`} label={t('backOffice_advisors_extProducts')} />
                    </Grid>
                  </Grid>
                </FormWithoutEnterSubmit>
              );
            }}
          </Formik>
        </DialogContent>
        <DialogActions>
          <ControlButtons
            isSubmitting={formSubmitting}
            nextText={t('general_save')}
            form={formKey}
            backText={t('general_cancel')}
            onBack={handleClose}
            onClick={() => {}}
          />
        </DialogActions>
      </Dialog>
    </>
  ) : null;
};

export default compose(observer)(EditAdvisorModal);
